import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { doc, getDoc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import { useWallet } from '../context/WalletContext';

export function DonationPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { updateBalance, handleAnonymousDonation } = useWallet();
  const [amount, setAmount] = useState<number>(0);
  const [displayName, setDisplayName] = useState<string>('');
  const [request, setRequest] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [processing, setProcessing] = useState(false);
  const isAnonymous = location.pathname.includes('/anonymous');

  useEffect(() => {
    const fetchRequest = async () => {
      if (!id) return;
      
      try {
        const requestDoc = await getDoc(doc(db, '2dr_requests', id));
        if (requestDoc.exists()) {
          setRequest({ id: requestDoc.id, ...requestDoc.data() });
        } else {
          setError('Demande introuvable');
        }
      } catch (error) {
        console.error('Erreur:', error);
        setError('Erreur lors du chargement de la demande');
      } finally {
        setLoading(false);
      }
    };

    fetchRequest();
  }, [id]);

  const incrementDonorCount = async () => {
    if (!id) return;

    try {
      const requestRef = doc(db, '2dr_requests', id);
      await updateDoc(requestRef, {
        uniqueDonorsCount: increment(1)
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour du compteur de donateurs:', error);
    }
  };

  const handleDonate = async () => {
    if (!request || processing) return;
    
    if (amount < (request.minDonationAmount || 100)) {
      setError(`Le montant minimum est de ${request.minDonationAmount || 100} FCFA`);
      return;
    }

    setProcessing(true);
    setError('');

    try {
      let success;
      
      if (isAnonymous) {
        // Don anonyme avec nom optionnel
        success = await handleAnonymousDonation(amount, request.id, request.userId, displayName);
        if (success) {
          await incrementDonorCount();
        }
      } else if (user?.id) {
        // Don authentifié
        success = await updateBalance(-amount, request.id);
      }

      if (success) {
        navigate(`/2dr/request/${id}`);
      } else {
        setError('Erreur lors du don');
      }
    } catch (error: any) {
      setError(error.message || 'Erreur lors du don');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-black p-4">
      <p className="text-gray-400 text-center">Chargement...</p>
    </div>;
  }

  if (error) {
    return <div className="min-h-screen bg-black p-4">
      <p className="text-red-500 text-center">{error}</p>
    </div>;
  }

  if (!request) {
    return <div className="min-h-screen bg-black p-4">
      <p className="text-gray-400 text-center">Demande introuvable</p>
    </div>;
  }

  return (
    <div className="min-h-screen bg-black">
      <div className="bg-black/50 text-white p-4 flex items-center justify-between sticky top-0 z-10 backdrop-blur-lg border-b border-white/10">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-lg font-bold">Donation</h1>
        <div className="w-6" />
      </div>

      <div className="container mx-auto px-4 py-8 max-w-md">
        <div className="bg-white/5 backdrop-blur-lg rounded-3xl p-6 border border-white/10">
          <div className="mb-6">
            <h2 className="text-xl font-bold text-white mb-2">{request.title}</h2>
          </div>

          <div className="space-y-6">
            {isAnonymous && (
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Nom (optionnel)
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Laissez vide pour rester anonyme"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Montant (FCFA)
              </label>
              <input
                type="number"
                min={request.minDonationAmount || 100}
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Entrez le montant"
              />
              <p className="text-sm text-gray-400 mt-2">
                Minimum: {request.minDonationAmount || 100} FCFA
              </p>
            </div>

            {error && (
              <p className="text-red-500 text-sm">{error}</p>
            )}

            <button
              onClick={handleDonate}
              disabled={processing || amount < (request.minDonationAmount || 100)}
              className="w-full bg-green-500 hover:bg-green-600 disabled:bg-green-500/50 disabled:cursor-not-allowed text-white font-semibold px-6 py-3 rounded-xl transition-colors"
            >
              {processing ? 'Traitement...' : 'Donner'}
            </button>

            {isAnonymous && (
              <p className="text-sm text-center text-gray-400">
                Votre don sera affiché comme anonyme
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 